import React from 'react'
import { Stack } from 'react-bootstrap'
import { useFormikContext } from 'formik'
import type { PivotFilterFormValues } from '../pivot/pivotFilterForm.utils'
import { PivotFieldTypeEnum, PivotFilterOperatorEnum } from '@ztechapps/common.contract'
import { isPivotRangeOperator, isPivotRelativeOperator } from '@ztechapps/common.utils'
import { Field } from '@ztechapps/webui.form'
import { ShowIf } from '@ztechapps/webui.util'
import { PIVOT_CONDITION_OPERATOR_OPTIONS, PIVOT_RELATIVE_UNIT_OPTIONS } from '../pivot/pivotFilterOperator.utils'
import { PivotFilterSectionEnum, PivotFilterSelect, PivotOperandFields } from './PivotFilterFields'

/** Props for the Condition pane of the filter dialog. */
export type PivotFilterConditionPaneProps = {
  /** A string, number or ungrouped date field — the other fields filter by selection. */
  fieldType: PivotFieldTypeEnum
}

/** The Condition kind — an operator offered by the field's type, then its one or two operands. */
export const PivotFilterConditionPane = ({ fieldType }: PivotFilterConditionPaneProps) => {
  const { values } = useFormikContext<PivotFilterFormValues>()
  const { operator } = values.condition
  const isRelative = isPivotRelativeOperator(operator)
  const operatorHint = isRelative
    ? 'Relative to today. A week starts on Monday.'
    : isPivotRangeOperator(operator)
      ? operator === PivotFilterOperatorEnum.between
        ? 'Both bounds are included.'
        : 'The bounds themselves are left out.'
      : fieldType === PivotFieldTypeEnum.String
        ? 'Text is compared ignoring case.'
        : ''

  return (
    <Stack gap={2}>
      <Field
        name="condition.operator"
        label="Operator"
        component={PivotFilterSelect}
        options={PIVOT_CONDITION_OPERATOR_OPTIONS[fieldType]}
        onChange
        help={`${operatorHint} A blank member never passes a condition.`.trim()}
        showHelpAsInline
      />
      <ShowIf condition={isRelative}>
        <Field
          name="condition.unit"
          label="Unit"
          component={PivotFilterSelect}
          options={PIVOT_RELATIVE_UNIT_OPTIONS}
          onChange
        />
      </ShowIf>
      <ShowIf condition={!isRelative}>
        <PivotOperandFields section={PivotFilterSectionEnum.condition} operator={operator} valueType={fieldType} />
      </ShowIf>
    </Stack>
  )
}
